import clsx from 'clsx';
import React, { type FC, useCallback, useMemo, useState } from 'react';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { CodeBlock } from '@fragment/ui/src/components/CodeBlock/CodeBlock';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

type ApiClientCreatedProps = {
  clientId: string;
  clientSecret: string;
  initialTab?: 'json' | 'cli';
};

export const ApiClientCreatedView: FC<ApiClientCreatedProps> = ({
  clientId,
  clientSecret,
  initialTab = 'json',
}) => {
  const [activeTab, setActiveTab] = useState<'json' | 'cli'>(initialTab);
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  // Key names match createFragmentClient's `params`, so the JSON pastes
  // directly into an SDK initialization.
  const data = useMemo(
    () => ({
      clientId,
      clientSecret,
      apiUrl: activeWorkspaceFromContext?.pod.customerApiUrl ?? '',
      authUrl: `https://${activeWorkspaceFromContext?.pod.cognitoAuthDomain}/oauth2/token`,
      scope: activeWorkspaceFromContext?.pod.customerApiOAuthScope ?? '',
    }),
    [clientId, clientSecret, activeWorkspaceFromContext]
  );

  const cliLoginCommand = useMemo(() => {
    const { apiUrl, authUrl, scope } = data;
    return `fragment login \\
    --client-id ${clientId} \\
    --client-secret ${clientSecret} \\
    --api-url ${apiUrl} \\
    --oauth-url ${authUrl} \\
    --oauth-scope "${scope}"`;
  }, [data, clientId, clientSecret]);

  const handleTabChange = useCallback((tab: 'json' | 'cli') => {
    setActiveTab(tab);
  }, []);

  return (
    <div className={clsx('flex flex-col space-y-f2', 'max-w-full ')}>
      <div data-testid="api-client-information-section">
        <Toggle
          leftLabel="JSON"
          rightLabel="CLI"
          initialActiveButton={initialTab === 'cli' ? 'right' : 'left'}
          onLeftClick={() => handleTabChange('json')}
          onRightClick={() => handleTabChange('cli')}
          buttonHeight="h-fit"
          buttonPadding="px-[1ch]"
          width="w-fit"
          inline
        />

        {activeTab === 'json' ? (
          <CodeBlock language="json">{JSON.stringify(data, null, 2)}</CodeBlock>
        ) : (
          <CodeBlock language="bash">{cliLoginCommand}</CodeBlock>
        )}
      </div>
      <ErrorText>clientSecret will not be shown again.</ErrorText>
    </div>
  );
};
